/**
 * Authentication Types
 *
 * Shared type definitions for the authentication system.
 * These types are used across AuthProvider and its extracted hooks.
 */

import type { UserProfileResponse } from '@models';
import type { DirectSignInOptions, Prompt } from '@logto/js';
import type { CreateEventContextType } from '@lib/analytics-events';

// =============================================================================
// SESSION STATE
// =============================================================================

/**
 * Session state - simplified from previous state machine
 * SDK handles token refresh internally, we only track high-level states
 */
export enum AuthSessionState {
  /** SDK loading, state unknown */
  INITIALIZING = 'initializing',
  /** Not logged in, guest mode */
  UNAUTHENTICATED = 'unauthenticated',
  /** Logged in (SDK handles refresh automatically) */
  AUTHENTICATED = 'authenticated',
  /** AIDEV-NOTE: Attempting silent re-auth with prompt=none */
  REAUTHENTICATING = 'reauthenticating',
  /** Refresh token expired, redirecting to login */
  SESSION_EXPIRED = 'session_expired',
}

// =============================================================================
// ERROR TYPES
// =============================================================================

/**
 * Shape of Logto SDK errors
 */
export interface LogtoErrorShape {
  code?: string;
  error?: string;
  message?: string;
}

/**
 * Extended Logto error with nested properties
 */
export interface LogtoExtendedError extends LogtoErrorShape {
  cause?: LogtoErrorShape;
  name?: string;
  data?: LogtoErrorShape;
}

// =============================================================================
// USER DATA TYPES
// =============================================================================

/**
 * Cached user info for UI flash prevention
 */
export interface CachedUserInfo {
  id: string | null;
  slug: string | null;
  full_name: string | null;
  profile_picture: string | null;
}

// =============================================================================
// CONTEXT TYPE
// =============================================================================

/**
 * Options accepted by `signIn`.
 *
 * SINGLE SOURCE OF TRUTH. This shape used to be written out three times -- here,
 * on `UseSignInOutReturn['signIn']`, and again on the inline parameter type of
 * the `useCallback` in useSignInOut.ts -- with the long-form `prompt` rationale
 * pasted verbatim into two of them. Only the innermost one actually bound, so a
 * field added to one and not the others failed silently. All three now reference
 * this interface; add new options here and nowhere else.
 *
 * AIDEV-NOTE: signIn accepts optional loginHint for email prefill (used in guest
 * question submission).
 * AIDEV-NOTE: actionType tracks what guest action triggered auth (e.g.,
 * "i_can_help", "me_too"). See LoginPromptActionType in useLoginPrompt.ts for
 * the valid action types.
 */
export interface SignInOptions {
  loginHint?: string;
  actionType?: string;
  /** Which surface a create-event sign-in started from; analytics only. */
  contextType?: CreateEventContextType;
  /** Which Logto experience screen opens first. Defaults to 'register'.
   * The only production call site that ever passed 'signIn' —
   * CommunityWelcomeSlide's guest-only "Sign in" button (handleSignIn) — was
   * deleted on 2026-07-30 along with the button itself: onboarding is
   * guest-only, and the welcome flow's secondary slot now holds a
   * "Continue as guest" exit that never touches Logto. The live 'signIn' call
   * sites today are all on the magic-link flow: AuthDoor's flag-off email
   * submit and its "Use a code instead", plus AuthLinkLanding's redemption --
   * each with its own adjacent comment on why Create Account is the wrong
   * screen for someone whose address demonstrably already exists.
   * AIDEV-TODO: app-wide audit — every other "Log in"-labeled call site
   * (GuestLayout, CommunityDetail, Events, Chat, QuestionDetails,
   * ProfilePage, InvitePage, SignedOutByOtherTabDrawer, InviteGate,
   * InviteAcceptDrawer, EventGuestPage) still lands on register; each
   * should pass 'signIn'. The plumbing is live end-to-end (useSignInOut
   * -> logtoSignIn on web, NativeLogtoAdapterProvider's
   * toInteractionMode on native), so the audit only needs call-site
   * edits. Note the audit's shape changed under it: with
   * `featureFlags.magicLinkAuth` on, every one of those call sites is
   * rerouted to /auth by `routeToDoor` (useSignInOut.ts) and never reaches
   * logtoSignIn at all, so only the flag-off and `bypassDoor` paths still
   * consult `firstScreen`. Still unverified against the live tenant: that the Logto
   * sign-in experience actually honors 'signIn' as "open the login form
   * directly" is a QA item, not a settled fact (onboarding-design §18).
   *
   * AIDEV-NOTE [MEETBALL magic-link]: 'identifier:sign_in' is the OTP fast path.
   * It opens Logto's `/identifier-sign-in` screen instead of the branded
   * landing, and the fork treats that screen plus `identifiers: ['email']` plus
   * a valid `loginHint` as "submit this address for me", so the code is sent on
   * arrival. See the long note on AuthDoor's handleUseCode for why this and not
   * a custom query param. */
  firstScreen?: 'signIn' | 'register' | 'identifier:sign_in';
  /** Which identifier types the `identifier:sign_in` / `identifier:register`
   * screens should offer, forwarded verbatim to the Logto SDK as the
   * `identifier` authorization param (web path only; the native adapter has no
   * slot for it and ignores it). Ignored by Logto unless `firstScreen` is one
   * of those two screens.
   *
   * AIDEV-NOTE [MEETBALL magic-link]: half of the OTP fast-path signal. The
   * logto-ui fork auto-submits only when this is exactly `['email']`, so
   * widening it at a call site silently turns the auto-submit off rather than
   * breaking anything. */
  identifiers?: ('email' | 'phone' | 'username')[];
  /** Forwarded verbatim to the Logto SDK's signIn call (web path only; the
   * native adapter's signIn has no slot for these and ignores them). */
  extraParams?: Record<string, string>;
  /** Forwarded verbatim to the Logto SDK's signIn call (web path only). */
  directSignIn?: DirectSignInOptions;
  /** Overrides the `prompt` on the authorization request (web path only;
   * absent means the SDK's own default, `Prompt.Consent`).
   *
   * AIDEV-NOTE [MEETBALL magic-link]: two callers redeem a one-time token and
   * both pass `[Prompt.Login, Prompt.Consent]`, on EVERY gate:
   * AuthLinkLanding's `handleConfirm`, the one redeem `signIn` that `confirm`,
   * `confirm_unknown_identity`, `identity_timeout`, `same_account` and
   * `switch_account` all reach, and AuthDoor's continuation claim.
   *
   * Why `login`: the webapp can be signed out locally while Logto's own session
   * is still live (`transitionToGuestSession` clears local state and never
   * signs out at Logto), so no gate can assume there is no session to
   * displace. With that session belonging to a DIFFERENT account and
   * `prompt=consent` alone, no login interaction runs, but the consent
   * interaction reaches Logto's consent guard
   * (packages/core/src/middleware/koa-consent-guard.ts). The guard finds that
   * the session's account is not the link's address and redirects to its
   * switch-account screen with the token still unspent, where the logto-ui
   * fork's "Continue as" redeems it as the link's account. So without `login`
   * the cost is an extra screen, never the wrong account. `prompt=login`
   * forces re-authentication instead: the token signs in its own account in
   * place of that session, a silent switch. On a fresh browser it adds no
   * screen, because the one-time token completes the login interaction on its
   * own. When the live session is the link's OWN account (the `same_account`
   * gate), `login` changes nothing visible: the guard finds the match and
   * either spends the token itself or routes it to the one-time-token screen,
   * so it is spent either way.
   *
   * `consent` stays beside it, for two reasons. First, a bare `prompt=login`
   * returns no refresh token, because `offline_access` is granted when consent
   * resolves, and this app leans on refresh tokens for every silent renewal.
   * An array is joined with a space by @logto/js's `buildPrompt`
   * (lib/core/sign-in.js), so both travel as one `prompt` param. Second, the
   * consent interaction is what makes the consent guard run at all, which makes
   * it the backstop for `login`: with neither prompt, a live session for
   * another account that already holds a grant raises no interaction, skips the
   * guard entirely, and that account is signed in instead of the link's. So
   * `consent` must never be dropped from these calls.
   *
   * Never pass `Prompt.None` through here. It means silent authentication, the
   * wrong semantics on any user-facing screen (a visitor who needs to sign in
   * gets an error instead of a form), and the recovery paths that legitimately
   * want it -- useInvalidGrantHandler's attempts -- call `logtoSignIn` directly
   * and never go through this wrapper. */
  prompt?: Prompt | Prompt[];
  /** Web-only escape hatch from the magic-link door: skips the `/auth`
   * redirect and calls the Logto SDK directly even when
   * `featureFlags.magicLinkAuth` is on. Set by AuthDoor's own call sites
   * (its social buttons and "Use a code instead") -- they're already ON
   * the door, so routing them back to `/auth` would be a silent no-op.
   * Ignored on native, where the flag never routes to the door anyway. */
  bypassDoor?: true;
  /** Leaves the "Signed in as ..." cue armed across this sign-in. Every other
   * sign-in that reaches Logto clears it first (AuthProvider wraps the SDK's
   * `signIn` so that no path can start one without clearing, see
   * `logtoSignIn` there and `signedInCueSlot.clear()`).
   *
   * AIDEV-NOTE [MEETBALL magic-link]: for the redemptions the cue was armed
   * FOR, and nothing else: AuthDoor's continuation redemption and
   * AuthLinkLanding's `handleConfirm` on the gates that did not name the
   * signed-in account. Passing it anywhere else can only let an older cue
   * survive into this sign-in, i.e. one redundant "Signed in as <the account
   * the visitor is really in>"; it can never suppress a cue. Ignored on the
   * door-routing branch, which navigates and starts nothing. */
  keepSignedInCue?: true;
  /** Called once the underlying sign-in attempt settles, with how it settled.
   * Callers use it to clear local "signing in" busy state that would otherwise
   * wedge indefinitely, and `/auth/link` uses the outcome to tell a failed
   * sign-in from a slow one.
   *
   * `rejected` is a VERDICT. On web the attempt threw before any navigation
   * started (the OIDC config fetch, PKCE generation, or a storage write -- the
   * sign-in session or the token clear), so nothing is in flight and a retry
   * cannot race anything. That only reaches here because
   * `SignInSurfacingLogtoClient` re-raises what @logto/react's `useLogto().signIn`
   * swallows; `window.location.assign()` itself never throws. On native it is a
   * failure of the system-browser session. It
   * carries the error, and never a user cancellation: the native adapter
   * resolves on cancellation and the web failures are all pre-navigation
   * errors.
   *
   * `resolved` is NOT a verdict. On web a success resolves once
   * `location.assign()` has been called, which is the START of the navigation:
   * the calling document stays alive until the replacement document COMMITS
   * (the prod-measured gap is `SIGN_IN_FAILURE_REVEAL_MS`), so a redirect that
   * is working and one that is hung both read `resolved` here. On native a
   * user-cancelled sign-in resolves too, because NativeLogtoAdapterProvider
   * early-returns on cancellation. Treat it as "nothing failed that we know
   * of", never as "signed in".
   *
   * Deliberately an additive option rather than a `Promise` return type on
   * signIn: ~10 call sites invoke signIn as fire-and-forget, and handing them a
   * promise nobody awaits would turn every native rejection into an unhandled
   * rejection. Callers guard with a mounted ref, since it can land after the
   * screen is gone.
   *
   * AIDEV-NOTE [MEETBALL magic-link]: on the door branch signIn performs a
   * client-side navigation to /auth with nothing to await, so this fires
   * SYNCHRONOUSLY with `resolved`, right after the navigate. Callers doing
   * teardown (closing a drawer, clearing a draft) therefore run before the door
   * has mounted. Every current caller is order-insensitive; if you add one that
   * is not, do not rely on this ordering. */
  onSignInSettled?: (outcome: SignInOutcome) => void;
}

/** How a sign-in attempt settled. See `SignInOptions.onSignInSettled`. */
export type SignInOutcome = { kind: 'resolved' } | { kind: 'rejected'; error: unknown };

/**
 * Centralized Authentication Context Interface
 * This is the single source of truth for authentication state
 */
export interface AuthContextType {
  // Core authentication state
  isAuthenticated: boolean;
  showAuthenticatedUI: boolean;
  isLoading: boolean;
  error: string | null;
  user: UserProfileResponse | null;
  cachedUserInfo: CachedUserInfo | null;

  // AIDEV-NOTE: Observable session state for components/hooks to check before API calls
  sessionState: AuthSessionState;
  isSessionExpired: boolean; // Convenience flag for session_expired | reauthenticating

  // AIDEV-NOTE: Partial auth detection - user has Logto tokens but no profile in our backend
  // Used by CreateQuestion to skip Logto redirect and go directly to /signup
  hasLogtoSession: boolean;

  /**
   * Whether this page load follows a sibling tab's sign-out. Captured once, in AuthProvider's
   * first render; constant for its lifetime. Always false on native.
   */
  signedOutByOtherTabAtMount: boolean;

  // Authentication actions
  signIn: (options?: SignInOptions) => void;
  signOut: () => Promise<void>;

  // Helper methods
  getAuthToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
}

// =============================================================================
// REDUCER TYPES
// =============================================================================

/**
 * Auth reducer state
 */
export interface AuthReducerState {
  sessionState: AuthSessionState;
}

/**
 * Auth reducer actions
 */
export type AuthAction =
  | { type: 'SET_AUTHENTICATED' }
  | { type: 'SET_UNAUTHENTICATED' }
  | { type: 'SET_REAUTHENTICATING' }
  | { type: 'SET_SESSION_EXPIRED' }
  | { type: 'SET_STATE'; payload: AuthSessionState };

// =============================================================================
// TIMING CONSTANTS
// =============================================================================

/** Buffer time before token expiry to trigger proactive refresh */
export const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

/** Delay before retrying on network error during silent auth */
export const NETWORK_RETRY_DELAY_MS = 1000;
